import {
  MAX_CARD_NUMBER_LENGTH,
  MAX_CVV_LENGTH,
  MAX_EXPIRY_DATE_LENGTH,
  EXPIRY_MONTH_POSITION,
} from '@ordermentum/ui-constants';

/**
 * @description This functions parsed and validates input from an input box and returns value as "MM/YY"
 * @override input's target value
 * @example A user can only start with 0 or 1 (months), after the month is typed, this modifier adds a '/' to the input
 * and then user can enter the year. For eg. 12/22
 * @returns "MM/YY" to the input target value
 *
 * @param {string} expiryDate
 */
export const expiryDateModifier = (expiryDate: string): string => {
  if (!/^[0-9/]*$/.test(expiryDate)) {
    return expiryDate.substr(0, expiryDate.length - 1);
  }
  const matches = expiryDate.match(/\//g);
  if (expiryDate.length > MAX_EXPIRY_DATE_LENGTH) {
    return expiryDate.substr(0, MAX_EXPIRY_DATE_LENGTH);
  }
  if (
    expiryDate.substr(0, EXPIRY_MONTH_POSITION) > '12' ||
    expiryDate.substr(0, EXPIRY_MONTH_POSITION) === '00'
  ) {
    return expiryDate.substr(0, expiryDate.length - 1);
  }
  if (
    (matches && matches.length > 1) ||
    (matches && expiryDate.indexOf('/') !== EXPIRY_MONTH_POSITION)
  ) {
    return expiryDate.substr(0, expiryDate.indexOf('/'));
  }
  if (
    expiryDate.length > EXPIRY_MONTH_POSITION &&
    // eslint-disable-next-line no-bitwise
    !~expiryDate.indexOf('/')
  ) {
    return expiryDate.replace(/(\d{2})/, '$1/');
  }
  return expiryDate;
};

/**
 * @description This functions parsed and validates input from an input box and returns value as "XXXX XXXX XXXX XXXX"
 * @override input's target value
 * @example Typed value is seperated by a space till we reach 20 digits.
 * @returns "XXXXXXXXXXXXXXXXXXX" is returned as "XXXX XXXX XXXX XXXX" to the input target value
 *
 * @param {string} cardNumber
 */
export const splitCardNumber = (cardNumber: string) =>
  cardNumber.replace(/(\d{4})/g, '$1 ').trim();

/**
 * @description This functions parsed and validates input from an input box and returns value as "XXXX XXXX XXXX XXXX". It also restricts input to just to numbers only
 * @override input's target value
 * @example Typed value is seperated by a space till we reach 20 digits.
 * @returns "XXXXXXXXXXXXXXXXXXX" is returned as "XXXX XXXX XXXX XXXX" to the input target value
 *
 * @param {string} cardNumber
 */
export const creditCardNumberModifier = (cardNumber: string) => {
  const number = cardNumber.replace(/\s/g, '');
  const containsInvalidCharacter = /[^0-9]$/.test(number);

  if (containsInvalidCharacter || number.length > MAX_CARD_NUMBER_LENGTH) {
    return cardNumber.replace(/(.$)/, '');
  }
  return splitCardNumber(number);
};

/**
 * @description This functions parsed and validates input from an input box and returns a trimmed cvv value
 * @param {string} cvv
 */
export const cvvModifier = (cvv: string) => {
  const number = cvv.replace(/\s/g, '');
  if (number.length > MAX_CVV_LENGTH) {
    return cvv.replace(/(.$)/, '');
  }
  return number;
};

/**
 *
 * @description Convert the first file out of an input type file that accepts image to base64 and calls the callback with the base64 string if available
 * @param {(FileList | null)} [files]
 * @param {(imagePath: string, fileName: string) => void} [callback]
 * @returns
 */
export const imageFileToBase64 = (
  files?: FileList | null,
  callback?: (imagePath: string | ArrayBuffer, fileName: string) => void
) => {
  if (!files || files.length === 0) {
    return;
  }
  const file = files[0];
  if (file.type.startsWith('image/')) {
    const reader = new FileReader();
    reader.onload = (() => e => {
      const imagePath = e.target?.result;
      if (callback && imagePath) {
        callback(imagePath, file.name);
      }
    })();
    reader.readAsDataURL(file);
  }
};

/**
 * @description This function only allows alphanumeric values
 * @override input's target value
 * @example "**TEST**" is returned back as "TEST"
 *
 * @param {string} value
 */
export const alphaNum = (value: string) => value.replace(/[^a-zA-Z0-9]/gi, '');

/**
 * @description This function only allow decimal values b/w 0.00 and 100.00
 *
 * @param {string} value
 */
export const percentageValue = (value: string) => {
  const match = value.match(/(^[0-9][0-9]?)(\.[0-9]{0,2})?/gi);
  return match?.[0] || '';
};
